<template>
  <div>
    <Dialog v-model:visible="visible" modal :closable="false" :header="title" :style="{ width: '50vw' }" :breakpoints="{ '1199px': '75vw', '575px': '90vw' }">
      <div class="flex flex-col">
        <Breadcrumb v-if="categoryBreadcrumbsPath.length > 0" :home="breadcrumbsHome" :model="categoryBreadcrumbsPath" />
        <label class="text-sm">Name</label>
        <StdPropEditor class="col-span-4" :prop-def="categoryPropDefs['name']" v-model="categoryCopy.name" />
        <label class="text-sm mt-4">Description</label>
        <StdPropEditor class="col-span-4" :prop-def="categoryPropDefs['description']" v-model="categoryCopy.description" />
        <label class="text-sm mt-4">KPIs</label>
        <label class="text-xs mt-1 mb-2">Only analyze selected KPIs for this Category and child categories. Keep blank to analyze all. </label>
        <StdItemMultiSelect v-model="categoryCopy.kpis" :availableItems="categoriesStore.catKpis" :disableAddItem="true"></StdItemMultiSelect>
        <!--
        <label>TODO Tags</label>
        <MultiSelect v-model="hcSubdomainsSelected" :options="hcSubdomains" placeholder="Select Subdomains" :maxSelectedLabels="3" class="w-full" />
        -->
      </div>
      <template #footer>
        <Button label="Cancel" severity="secondary" @click="visible = false" autofocus />
        <Button label="Save" :disabled="!saveEnabled" severity="primary" @click="onSave" autofocus />
      </template>
    </Dialog>
  </div>
</template>

<script setup>
  import { useRbacStore } from '@/store/RBACStore';
  import { useKbStore } from '@/store/KbStore';
  import { useCategoriesStore } from '@/store/CategoriesStore';
  import { useToast } from 'vue-toastification';
  import { ref, reactive, nextTick, computed } from 'vue';
  import InputText from 'primevue/inputtext';
  import { getUUID, pathPut } from '@enegelai/core';
  import Dialog from 'primevue/dialog';
  import Button from 'primevue/button';
  import Breadcrumb from 'primevue/breadcrumb';
  import Message from 'primevue/message';
  import MultiSelect from 'primevue/multiselect';
  import StdPropEditor from '@/components/Editors/StdPropEditor.vue';
  import StdItemMultiSelect from '@/components/Editors/StdItemMultiSelect.vue';

  const emit = defineEmits(['save']);

  const categoriesStore = useCategoriesStore();

  const visible = ref(false);

  const categoryKey = ref(null);
  const categoryParentKey = ref(null);
  const categoryParentName = ref(null);
  const categoryCopy = ref(reactive({}));
  const categoryBreadcrumbsPath = ref([]);
  const categoryKpiCollection = computed(() => categoriesStore.catKpiCollection);

  const title = computed(() => {
    if (categoryKey.value) {
      return `Edit Category ${categoryCopy.value.name}`;
    }
    return `New Category ${categoryCopy.value.name}`;
  });

  // Definition of editable category properties
  const categoryPropDefs = {
    name: { type: 'string', required: true, label: 'Name', placeholder: 'Give Category a self-explanatory name' },
    description: { type: 'text', rows: 4, required: false, label: 'Description', placeholder: 'Describe category in more details. Provide samples of messages that would fit this category, if needed.' },
    tags: { type: 'array', required: false, label: 'Tags', placeholder: 'Category tags' },
    kpis: { type: 'array', required: false, label: 'KPIs', placeholder: 'Category KPIs' },
  };

  const saveEnabled = computed(() => true);

  const breadcrumbsHome = ref({
    icon: 'pi pi-sitemap',
  });

  async function start(category, key = null, parentKey = null, parentName = null, breadcrumbsPath = []) {
    // Extract all category properties that we can edit in this dialog, set default values if not provided
    const categoryEditableProps = {
      id: category?.id || getUUID(),
      name: category?.name || '',
      description: category?.description || '',
      tags: category?.tags || [],
      kpis: category?.kpis || [],
    };

    // Make a deep reactive clone of the category object to avoid changing the original object
    categoryCopy.value = reactive(JSON.parse(JSON.stringify(categoryEditableProps)));
    categoryKey.value = key;
    categoryParentKey.value = parentKey;
    categoryParentName.value = parentName;
    categoryBreadcrumbsPath.value = breadcrumbsPath;

    visible.value = true;
  }

  function onSave() {
    emit('save', categoryCopy.value, categoryKey.value, categoryParentKey.value);
    visible.value = false;
  }

  defineExpose({ start });
</script>
<style>
  .p-breadcrumb {
    padding: 0px 0px 20px 0px;
  }
</style>
